import React, { useMemo } from 'react';
import Menu from 'components/Menu';
import Icon from 'components/Icon';
import useModal from 'hooks/useModal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteDocument } from 'queries/documents';
import toast from 'react-hot-toast';
import { readAxiosErr } from 'utils/misc';
import { useNavigate, useParams } from 'react-router-dom';
import Confirmation from 'components/Confirmation';
import Modal, { Variant as ModalVariant } from 'components/Modal';
import Button, {
  Variants as ButtonVariants,
  Size as ButtonSize,
} from 'components/Button';
import useMedia from 'hooks/useMedia';
import EditDoc from './EditDoc';
import EmailDoc from './EmailDoc';

type AppProps = {
  doc: Record<string, any>;
};

const isSignerSigned = (signer?: Record<string, any>) =>
  !!(signer?.date_signed || signer?.date_completed);

const getRepsToCheck = (signMeta?: Record<string, any>) => {
  const reps = (signMeta?.reps ?? []).filter(Boolean);
  return reps.length > 0 ? reps : [signMeta?.rep].filter(Boolean);
};

const Actions: React.FC<AppProps> = ({ doc }) => {
  const { jobId = '' } = useParams();
  const navigate = useNavigate();
  const [edit, showEdit, closeEdit] = useModal();
  const [email, showEmail, closeEmail] = useModal();
  const [confirmOpen, showConfirmModal, closeConfirmModal] = useModal();
  const [editWarning, showEditWarning, closeEditWarning] = useModal();
  const queryClient = useQueryClient();
  const { isMobile } = useMedia();

  const deleteDocumentMutation = useMutation(() => deleteDocument(doc.id), {
    onError: (e: any) => {
      toast.error(readAxiosErr(e));
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['job-checklist', jobId]);
      queryClient.invalidateQueries(['job-last-checklist', jobId]);
      queryClient.invalidateQueries(['documents']);
      queryClient.invalidateQueries(['all-documents', { job: jobId }]);
    },
  });

  const goToCombinedEditor = () =>
    navigate(`/jobs/${jobId}/combine-docs-editor/${doc.id}`);

  // A combined doc is considered signed once any signer has filled placements
  // (date_completed) or the whole contract has been signed (signed_date).
  // BE will void the existing signatures when the user proceeds with the edit.
  const isCombinedDocSigned =
    !!doc.is_combined_document &&
    (!!doc.signed_date ||
      !!doc.date_completed ||
      isSignerSigned(doc.sign_meta?.customer) ||
      getRepsToCheck(doc.sign_meta).some(isSignerSigned));

  const options = useMemo(() => {
    let _options: any[] = [
      {
        key: 'email',
        label: 'Email',
        icon: 'email',
        iconClass: 'text-primary',
        onClick: showEmail,
      },
      {
        key: 'edit',
        label: 'Edit',
        icon: 'edit',
        iconClass: 'text-primary',
        onClick: doc.is_combined_document
          ? isCombinedDocSigned
            ? showEditWarning
            : goToCombinedEditor
          : showEdit,
      },
    ];

    if (doc.is_active) {
      _options = [
        ..._options,
        {
          key: 'download',
          label: 'Download',
          icon: 'cloud-download',
          iconClass: 'text-primary',
          onClick: () => {
            window.open(doc.url, '_blank');
          },
          element: (styles: any) => (
            <div
              className={styles}
              onClick={(e) => {
                e.preventDefault();
                window.open(doc.url, '_blank');
              }}
            >
              Download
            </div>
          ),
        },
      ];
    }

    _options = [
      ..._options,
      {
        key: 'delete',
        label: 'Delete',
        icon: 'delete',
        iconClass: 'text-error',
        onClick: showConfirmModal,
      },
    ];
    return _options;
  }, [doc.is_active]);

  return (
    <>
      {isMobile ? (
        <Menu
          trigger={<Icon name="more" size={16} className="mt-[4px]" />}
          options={options}
        />
      ) : (
        <div className="v-center space-x-2">
          {options?.map((option) => (
            <div key={option.key}>
              <Icon
                name={option.icon}
                className={`${option.iconClass} cursor-pointer`}
                onClick={option.onClick}
                size={18}
              />
            </div>
          ))}
        </div>
      )}
      <Confirmation
        title="Delete Document?"
        message="Are you sure you want to delete this document? This can not be undone."
        open={confirmOpen}
        closeConfirm={closeConfirmModal}
        onClick={() => deleteDocumentMutation.mutate()}
        isLoading={deleteDocumentMutation.isLoading}
      />
      <Modal
        variant={ModalVariant.Center}
        open={editWarning}
        closeModal={closeEditWarning}
        maxWidth="max-w-[493px]"
      >
        <div className="space-y-4">
          <div>
            <div className="flex justify-end">
              <Icon
                name="close"
                size={16}
                className="cursor-pointer text-text"
                onClick={closeEditWarning}
              />
            </div>
            <div className="text-2xl font-bold text-text">Edit Document?</div>
          </div>
          <div className="text-base text-text">
            Modifying this combined document will void the existing signature.
            The signer will need to re-sign after your changes are saved.
          </div>
          <div className="flex items-center justify-end gap-4">
            <div>
              <Button
                label="Close"
                variant={ButtonVariants.Outline}
                size={ButtonSize.Medium}
                onClick={closeEditWarning}
              />
            </div>
            <div>
              <Button
                label="Continue"
                size={ButtonSize.Medium}
                onClick={() => {
                  closeEditWarning();
                  goToCombinedEditor();
                }}
              />
            </div>
          </div>
        </div>
      </Modal>
      <EditDoc open={edit} onClose={closeEdit} doc={doc} />
      <EmailDoc open={email} docId={doc.id} closeModal={closeEmail} />
    </>
  );
};

export default Actions;
